import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon, EmailIcon } from "@chakra-ui/icons";
import { FaBrain, FaCode, FaDatabase, FaRobot, FaRocket, FaUsers } from "react-icons/fa";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Navbar from "../components/utilities/navbar";
import Footer from "./utilities/footer";
import career from "../assets/career.png";
import { theme } from "../components/utilities/theme";

const MotionBox = motion.create(Box);

const roles = [
  {
    icon: FaRobot,
    title: "Automation Engineer",
    text: "Build agent workflows, tool integrations, evaluation examples, and automation paths.",
  },
  {
    icon: FaCode,
    title: "Full-Stack Developer",
    text: "Ship React interfaces, backend APIs, dashboards, and production-ready product modules.",
  },
  {
    icon: FaDatabase,
    title: "Data Automation Builder",
    text: "Create scraping, cleanup, sync, reporting, and knowledge workflows for product teams.",
  },
  {
    icon: FaBrain,
    title: "Product Technologist",
    text: "Translate messy client problems into MVP scopes, technical plans, and delivery steps.",
  },
];

const candidateSignals = [
  "You can reason through product goals before jumping into implementation.",
  "You are curious about assistants, APIs, data workflows, and automation.",
  "You care about reliable delivery, security, and clean handoffs.",
  "You can communicate tradeoffs clearly with teammates and clients.",
];

export default function Careers() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const {
    pageBg,
    altCardBg: cardBg,
    textColor,
    textSecondary,
    accentColor,
    accentCyan,
    contactInputBg: inputBg,
    errorColor,
  } = theme.colors;
  const phoneRegex = /^\d{10}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((previous) => ({ ...previous, phone: value || "" }));
  };

  const validate = () => {
    const validationErrors = {};

    if (!formData.name) validationErrors.name = "Name is required";
    if (!formData.email) validationErrors.email = "Email is required";

    if (!formData.phone) {
      validationErrors.phone = "Phone is required";
    } else {
      const digits = formData.phone.replace(/\D/g, "");
      const lastTen = digits.slice(-10);
      if (lastTen.length !== 10) {
        validationErrors.phone = "Phone number must be exactly 10 digits excluding country code";
      } else if (!phoneRegex.test(lastTen)) {
        validationErrors.phone = "Invalid phone format";
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      await emailjs.send(
        "service_5py02hk",
        "template_cvq0fjd",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        },
        "_O0GnOfQRkcf2GXsb"
      );

      toast({
        title: "Application Submitted",
        description: "Thank you. Our team will contact you by email to request your CV.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
      setErrors({});
    } catch {
      toast({
        title: "Submission Failed",
        description: "We could not submit your application. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box bg={pageBg} minH="100vh" color={textColor}>
      <Navbar />

      <MotionBox
        id="main-content"
        as="main"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={10} align="center">
            <Box flex="1.05">
              <Badge
                bg="rgba(0, 255, 208, 0.1)"
                color={accentCyan}
                border="1px solid rgba(0, 255, 208, 0.28)"
                borderRadius="full"
                px={4}
                py={1}
                mb={5}
              >
                Careers
              </Badge>
              <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.08" mb={5}>
                Join a team building useful products, not just feature tickets.
              </Heading>
              <Text color={textSecondary} fontSize={{ base: "md", md: "xl" }} lineHeight="1.8">
                We are looking for practical builders who can turn ambiguous
                workflows into useful applications, integrations, and automation
                systems.
              </Text>
            </Box>

            <Box
              flex="0.95"
              bg="rgba(15, 23, 42, 0.78)"
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={{ base: 6, md: 7 }}
              w="100%"
            >
              <Flex align="center" gap={4} mb={6}>
                <Flex
                  align="center"
                  justify="center"
                  w={14}
                  h={14}
                  borderRadius="lg"
                  bg="rgba(255, 111, 32, 0.14)"
                  border="1px solid rgba(255, 111, 32, 0.42)"
                >
                  <Icon as={FaRocket} color={accentColor} boxSize={7} />
                </Flex>
                <Box>
                  <Text color={textSecondary} fontSize="sm">
                    What matters here
                  </Text>
                  <Heading size="sm">Ownership, curiosity, and delivery</Heading>
                </Box>
              </Flex>
              <VStack align="stretch" spacing={3}>
                {candidateSignals.map((item) => (
                  <HStack key={item} align="start" spacing={3}>
                    <CheckCircleIcon color={accentCyan} mt="3px" />
                    <Text color={textSecondary}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>

        <Box bg="rgba(15, 23, 42, 0.72)" py={{ base: 12, md: 16 }}>
          <Container maxW="1360px" px={{ base: 4, md: 8 }}>
            <SectionHeading
              eyebrow="Open Tracks"
              title="Ways you can grow with us"
              text="Even when a specific role is not posted, we are interested in people who can help ship thoughtful products with care."
            />
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
              {roles.map((role) => (
                <Box
                  key={role.title}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={6}
                  minH="100%"
                >
                  <Icon as={role.icon} color={accentColor} boxSize={7} mb={5} />
                  <Heading size="sm" mb={3}>
                    {role.title}
                  </Heading>
                  <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                    {role.text}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">
            <Box
              as="form"
              onSubmit={handleSubmit}
              bg="rgba(15, 23, 42, 0.78)"
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={{ base: 6, md: 7 }}
            >
              <Heading size="lg" mb={2}>
                Apply to Orvitrix
              </Heading>
              <Text color={textSecondary} mb={6}>
                Share your details and paste your cover letter below. After submission,
                our team will contact you by email to request your CV.
              </Text>
              <HStack
                align="start"
                spacing={3}
                mb={6}
                p={4}
                bg="rgba(0, 255, 208, 0.06)"
                border="1px solid rgba(0, 255, 208, 0.18)"
                borderRadius="lg"
              >
                <EmailIcon color={accentCyan} mt="3px" />
                <Box>
                  <Text color={textSecondary} fontSize="sm">
                    Prefer email? Contact us at{" "}
                    <Link href="mailto:careers@orvitrix.com" color={accentCyan} fontWeight="bold">
                      careers@orvitrix.com
                    </Link>
                    .
                  </Text>
                </Box>
              </HStack>

              <Stack spacing={5}>
                <FormControl isInvalid={!!errors.name} isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    bg={inputBg}
                    color={textColor}
                    size="lg"
                    borderColor={errors.name ? errorColor : "rgba(255, 255, 255, 0.16)"}
                    _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                    _hover={{ borderColor: accentColor }}
                    _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isInvalid={!!errors.email} isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      bg={inputBg}
                      color={textColor}
                      size="lg"
                      borderColor={errors.email ? errorColor : "rgba(255, 255, 255, 0.16)"}
                      _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                      _hover={{ borderColor: accentColor }}
                      _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.phone} isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Box
                      sx={{
                        ".PhoneInput": { display: "flex", alignItems: "center", width: "100%" },
                        ".PhoneInputCountry": {
                          background: inputBg,
                          borderRadius: "8px 0 0 8px",
                          padding: "8px 10px",
                          border: errors.phone
                            ? `1px solid ${errorColor}`
                            : "1px solid rgba(255,255,255,0.16)",
                          borderRight: "none",
                          color: textColor,
                        },
                        ".PhoneInputCountrySelect": {
                          background: inputBg,
                          color: textColor,
                          border: "none",
                        },
                        ".PhoneInputCountrySelect option": {
                          background: inputBg,
                          color: textColor,
                        },
                        ".PhoneInputInput": {
                          width: "100%",
                          background: inputBg,
                          padding: "12px",
                          borderRadius: "0 8px 8px 0",
                          border: errors.phone
                            ? `1px solid ${errorColor}`
                            : "1px solid rgba(255,255,255,0.16)",
                          color: textColor,
                          fontSize: "16px",
                          outline: "none",
                        },
                        ".PhoneInputInput:focus": {
                          borderColor: accentColor,
                          boxShadow: `0 0 0 1px ${accentColor}`,
                        },
                      }}
                    >
                      <PhoneInput
                        international
                        defaultCountry="IN"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        name="phone"
                      />
                    </Box>
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <FormControl>
                  <FormLabel>Message / Cover Letter</FormLabel>
                  <Textarea
                    name="message"
                    placeholder="Paste your cover letter here and tell us what you like building, what you are learning, and where you can contribute."
                    value={formData.message}
                    onChange={handleChange}
                    bg={inputBg}
                    color={textColor}
                    rows={5}
                    size="lg"
                    borderColor="rgba(255, 255, 255, 0.16)"
                    _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                    _hover={{ borderColor: accentColor }}
                    _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  bg={accentColor}
                  color="white"
                  rightIcon={<ArrowForwardIcon />}
                  size="lg"
                  alignSelf={{ base: "stretch", sm: "flex-start" }}
                  borderRadius="lg"
                  isLoading={isSubmitting}
                  loadingText="Submitting"
                  _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
                >
                  Apply Now
                </Button>
              </Stack>
            </Box>

            <Stack spacing={5}>
              <Box
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
              >
                <Icon as={FaUsers} color={accentCyan} boxSize={8} mb={4} />
                <Heading size="md" mb={3}>
                  What we value
                </Heading>
                <Text color={textSecondary} lineHeight="1.8">
                  Clear thinking, careful execution, curiosity about automation, and the
                  humility to test ideas against real user workflows.
                </Text>
              </Box>
              <Box display={{ base: "none", lg: "block" }}>
                <Image
                  src={career}
                  alt="Careers at Orvitrix"
                  borderRadius="xl"
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  boxShadow="0 18px 44px rgba(0, 0, 0, 0.24)"
                  maxH="420px"
                  objectFit="contain"
                  mx="auto"
                />
              </Box>
            </Stack>
          </SimpleGrid>
        </Container>
      </MotionBox>

      <Footer />
    </Box>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  const { accentCyan, textSecondary } = theme.colors;

  return (
    <Box mb={8}>
      <Text color={accentCyan} fontWeight="bold" mb={2}>
        {eyebrow}
      </Text>
      <Heading size={{ base: "lg", md: "xl" }} mb={3}>
        {title}
      </Heading>
      <Text color={textSecondary} maxW="760px" lineHeight="1.8">
        {text}
      </Text>
    </Box>
  );
}
